import { GoogleLogin } from '@react-oauth/google';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../App.css';
import { Upload, Mail, CheckCircle, XCircle, AlertTriangle, FileText, Brain, Zap, Coins, LogOut, Globe, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Profile from './Profile';
import { loadRazorpayScript } from "../../utils/loadRazorpay";
import ExportCsvButton from '../ExportCsvButton.jsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
const APP_NAME = "AI EMAIL VALIDATOR"

const CURRENCY_RATES = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    INR: 83.5,
    AUD: 1.52,
    CAD: 1.36,
};

const CURRENCY_LABELS = {
    USD: 'USD - US Dollar',
    EUR: 'EUR - Euro',
    GBP: 'GBP - British Pound',
    INR: 'INR - Indian Rupee',
    AUD: 'AUD - Australian Dollar',
    CAD: 'CAD - Canadian Dollar',
};

const COUNTRY_TO_CURRENCY = {
    US: 'USD',
    CA: 'CAD',
    GB: 'GBP',
    UK: 'GBP',
    IN: 'INR',
    AU: 'AUD',
    NZ: 'AUD',
    SG: 'USD',
    DE: 'EUR',
    FR: 'EUR',
    ES: 'EUR',
    NL: 'EUR',
    IT: 'EUR',
    BE: 'EUR',
    DK: 'EUR',
    NO: 'EUR',
    SE: 'EUR',
    FI: 'EUR',
    IE: 'EUR',
};

const DEFAULT_CURRENCY = 'USD';

const getDefaultCurrency = () => {
    if (typeof navigator !== 'undefined') {
        const locale = navigator.language || 'en-US';
        const region = locale.split('-')[1];
        if (region && COUNTRY_TO_CURRENCY[region.toUpperCase()]) {
            return COUNTRY_TO_CURRENCY[region.toUpperCase()];
        }
    }
    return DEFAULT_CURRENCY;
};

function Home() {
    const [activePage, setActivePage] = useState('validate');
    const [activeTab, setActiveTab] = useState('single');
    const [singleEmail, setSingleEmail] = useState('');
    const [bulkEmails, setBulkEmails] = useState('');
    const [includeAI, setIncludeAI] = useState(true);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken'));
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [pricingPlans, setPricingPlans] = useState([]);
    const [pricingCurrency, setPricingCurrency] = useState(getDefaultCurrency());
    const [pricingLoading, setPricingLoading] = useState(false);
    const [defaultCredits, setDefaultCredits] = useState(0);
    const [activePurchasePlan, setActivePurchasePlan] = useState('');
    const fileInputRef = useRef(null);
    const isAuthenticated = Boolean(authToken);
    const navigate = useNavigate();

    const authHeaders = useMemo(
        () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        [authToken],
    );

    const currencyOptions = useMemo(
        () => Object.keys(CURRENCY_RATES).map((code) => ({ code, label: CURRENCY_LABELS[code] })),
        [],
    );

    const convertPrice = (usd) => {
        const rate = CURRENCY_RATES[pricingCurrency] || 1;
        return usd * rate;
    };

    const formatPrice = (usd) => {
        try {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: pricingCurrency || DEFAULT_CURRENCY,
            }).format(convertPrice(usd));
        } catch (error) {
            return `${convertPrice(usd).toFixed(2)} ${pricingCurrency}`;
        }
    };

    const handleCurrencyChange = (event) => {
        setPricingCurrency(event.target.value);
    };

    const handleSignOut = () => {
        localStorage.removeItem('authToken');
        setAuthToken(null);
        setUser(null);
        setStatusMessage(null);
        toast.info("Sign out successfull!")

        navigate("/signin")
    };

    const updateCreditsFromResponse = (response) => {
        const creditsHeader = response?.headers?.['x-credits-remaining'];
        if (!creditsHeader) {
            return;
        }
        const parsed = Number(creditsHeader);
        if (!Number.isNaN(parsed)) {
            setUser((prev) => (prev ? { ...prev, credits: parsed } : prev));
        }
    };

    const handleApiError = (error, fallbackMessage) => {
        console.error(fallbackMessage, error);
        const status = error?.response?.status;
        if (status === 401) {
            handleSignOut();
            return 'Your session expired. Please sign in again.';
        }
        if (status === 402) {
            setActivePage("pricing");
            return 'Insufficient credits. Visit the pricing page to top up.';
        }
        return error?.response?.data?.detail || fallbackMessage;
    };

    const handleGoogleLoginSuccess = async (credentialResponse) => {
        if (!credentialResponse?.credential) {
            setAuthError('Unable to retrieve Google credential.');
            return;
        }
        setAuthLoading(true);
        setAuthError('');
        try {
            const response = await axios.post(`${API}/auth/social-login`, {
                provider: 'google',
                token: credentialResponse.credential,
                currency_preference: pricingCurrency,
            });
            const { access_token: accessToken, user: userData } = response.data;
            localStorage.setItem('authToken', accessToken);
            setAuthToken(accessToken);
            setUser(userData);
            setStatusMessage({ type: 'success', text: `Welcome back, ${userData.name}!` });
            toast.success(`Welcome back, ${userData.name}!`);
        } catch (error) {
            const message = error?.response?.data?.detail || 'Google sign-in failed.';
            setAuthError(message);
            setStatusMessage({ type: 'error', text: message });
            toast.error({ message });

        } finally {
            setAuthLoading(false);
        }
    };

    const handleGoogleLoginError = () => {
        setAuthError('Google sign-in was cancelled.');
    };

    // const handlePurchasePlan = async (planId) => {
    //     if (!isAuthenticated) {
    //         const message = 'Sign in to purchase credits.';
    //         setAuthError(message);
    //         setStatusMessage({ type: 'error', text: message });
    //         return;
    //     }
    //     setPricingLoading(true);
    //     setActivePurchasePlan(planId);
    //     setStatusMessage(null);
    //     try {
    //         const response = await axios.post(
    //             `${API}/credits/purchase`,
    //             { plan_id: planId },
    //             { headers: authHeaders },
    //         );
    //         setUser(response.data);
    //         setStatusMessage({
    //             type: 'success',
    //             text: `Credits added! New balance: ${response.data.credits}.`,
    //         });
    //     } catch (error) {
    //         const message = handleApiError(error, 'Unable to complete purchase.');
    //         setStatusMessage({ type: 'error', text: message });
    //     } finally {
    //         setPricingLoading(false);
    //         setActivePurchasePlan('');
    //     }
    // };




    const handlePurchasePlan = async (planId) => {
        if (!isAuthenticated) {
            const message = "Sign in to purchase credits.";
            setAuthError(message);
            setStatusMessage({ type: "error", text: message });
            return;
        }

        const plan = pricingPlans.find((p) => p.plan_id === planId);
        if (!plan) return alert("Invalid plan!");

        setPricingLoading(true);
        setActivePurchasePlan(planId);
        setStatusMessage(null);

        const sdkLoaded = await loadRazorpayScript();
        if (!sdkLoaded) {
            setStatusMessage({ type: "error", text: "Failed to load payment SDK. Try again." });
            setPricingLoading(false);
            setActivePurchasePlan("");
            return;
        }

        try {
            const createResp = await axios.post(
                `${API}/credits/create-order`,
                { plan_id: planId },
                { headers: authHeaders }
            );

            const { order, razorpay_key_id } = createResp.data;
            if (!order || !order.id || typeof order.amount === "undefined") {
                throw new Error("Invalid order response from server");
            }


            const options = {
                key: razorpay_key_id,
                amount: order.amount,
                currency: order.currency || "INR",
                name: APP_NAME,
                description: `Purchase ${plan.name} Plan`,
                order_id: order.id,
                prefill: {
                    name: user?.name || "",
                    email: user?.email || "",
                    contact: user?.phone || ""
                },
                handler: async function (response) {
                    try {
                        const verifyResp = await axios.post(
                            `${API}/credits/verify`,
                            {
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_signature: response.razorpay_signature,
                                plan_id: planId
                            },
                            { headers: authHeaders }
                        );

                        // backend should return updated user object
                        setUser(verifyResp.data);
                        setStatusMessage({
                            type: "success",
                            text: `Payment successful! ${plan.credits.toLocaleString()} credits added. New balance: ${verifyResp.data.credits}.`,
                        });
                    } catch (err) {
                        const message = handleApiError(err, "Unable to verify payment.");
                        setStatusMessage({ type: "error", text: message });
                    } finally {
                        setPricingLoading(false);
                        setActivePurchasePlan("");
                    }
                },
                modal: {
                    ondismiss: function () {
                        // user closed the payment window
                        setPricingLoading(false);
                        setActivePurchasePlan("");
                    }
                }
            };

            const rzp = new window.Razorpay(options);

            // optional: subscribe to failure event for better UX
            rzp.on('payment.failed', function (resp) {
                const errorMsg = (resp && resp.error && resp.error.description) || "Payment failed";
                setStatusMessage({ type: "error", text: `Payment failed: ${errorMsg}` });
                setPricingLoading(false);
                setActivePurchasePlan("");
            });

            rzp.open();

        } catch (error) {
            console.error("Purchase error:", error);
            const message = handleApiError(error, "Unable to create order.");
            setStatusMessage({ type: "error", text: message });
            setPricingLoading(false);
            setActivePurchasePlan("");
        }
    };









    useEffect(() => {
        const loadPricing = async () => {
            setPricingLoading(true);
            try {
                const response = await axios.get(`${API}/pricing`);
                setPricingPlans(response.data?.plans || []);
                setDefaultCredits(response.data?.default_free_credits || 0);
            } catch (error) {
                setStatusMessage((prev) => prev ?? { type: 'error', text: 'Unable to load pricing at the moment.' });
            } finally {
                setPricingLoading(false);
            }
        };

        loadPricing();
    }, []);

    useEffect(() => {
        if (!authToken) {
            return;
        }
        let cancelled = false;

        const loadProfile = async () => {
            try {
                const response = await axios.get(`${API}/user/profile`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                });
                if (!cancelled) {
                    setUser(response.data);
                    if (response.data?.currency_preference) {
                        setPricingCurrency(response.data.currency_preference);
                    }
                }
            } catch (error) {
                const message = handleApiError(error, 'Unable to load profile.');
                setStatusMessage({ type: 'error', text: message });
            }
        };

        loadProfile();

        return () => {
            cancelled = true;
        };
    }, [authToken]);

    useEffect(() => {
        setStatusMessage(null);
    }, [activePage]);

    const validateSingleEmail = async () => {
        if (!singleEmail.trim()) {
            return;
        }

        if (!isAuthenticated) {
            const message = 'Please sign in to validate emails.';
            setResults({ type: 'error', data: { message } });
            setStatusMessage({ type: 'error', text: message });
            return;
        }

        setStatusMessage(null);
        setLoading(true);
        try {
            const response = await axios.post(
                `${API}/validate-email`,
                {
                    email: singleEmail.trim(),
                    include_ai_analysis: includeAI,
                },
                { headers: authHeaders },
            );
            updateCreditsFromResponse(response);
            setResults({ type: 'single', data: response.data });
            toast.success("Validation successful!");

        } catch (error) {
            const message = handleApiError(error, 'Validation failed.');
            setResults({
                type: 'error',
                data: { message },
            });
            toast.error("Validation Error!");
        }
        setLoading(false);
    };

    const validateBulkEmails = async () => {
        const emails = bulkEmails
            .split('\n')
            .map((email) => email.trim())
            .filter((email) => email);

        if (emails.length === 0) {
            return;
        }

        if (!isAuthenticated) {
            const message = 'Please sign in to validate emails.';
            setResults({ type: 'error', data: { message } });
            setStatusMessage({ type: 'error', text: message });
            return;
        }

        setStatusMessage(null);
        setLoading(true);
        try {
            const response = await axios.post(
                `${API}/validate-bulk`,
                {
                    emails,
                    include_ai_analysis: includeAI,
                },
                { headers: authHeaders },
            );
            updateCreditsFromResponse(response);
            setResults({ type: 'bulk', data: response.data });
            toast.success("Bulk Validation successful!");
        } catch (error) {
            const message = handleApiError(error, 'Bulk validation failed.');
            setResults({
                type: 'error',
                data: { message },
            });
        }
        setLoading(false);
    };

    const handleFileUpload = async (event) => {
        if (!isAuthenticated) {
            const message = 'Please sign in to upload files.';
            setResults({ type: 'error', data: { message } });
            setStatusMessage({ type: 'error', text: message });
            return;
        }

        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setUploadedFile(file);
        setStatusMessage(null);
        setLoading(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API}/upload-csv`, formData, {
                headers: {
                    ...authHeaders,
                    'Content-Type': 'multipart/form-data',
                },
            });
            updateCreditsFromResponse(response);
            setResults({ type: 'upload', data: response.data });
            toast.success("File Validation successful!");
        } catch (error) {
            const message = handleApiError(error, 'File upload failed.');
            setResults({
                type: 'error',
                data: { message },
            });
        }
        setLoading(false);
    };

    const getRiskColor = (riskLevel) => {
        switch (riskLevel) {
            case 'low': return 'text-green-600';
            case 'medium': return 'text-yellow-600';
            case 'high': return 'text-red-600';
            default: return 'text-gray-600';
        }
    };

    const getRiskIcon = (riskLevel) => {
        switch (riskLevel) {
            case 'low': return <CheckCircle className="w-5 h-5 text-green-600" />;
            case 'medium': return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
            case 'high': return <XCircle className="w-5 h-5 text-red-600" />;
            default: return <AlertTriangle className="w-5 h-5 text-gray-600" />;
        }
    };

    const renderSingleResult = (result) => (
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">Validation Result</h3>
                <div className="flex items-center space-x-2">
                    {getRiskIcon(result.risk_level)}
                    <span className={`font-semibold ${getRiskColor(result.risk_level)}`}>
                        {result.risk_level.toUpperCase()} RISK
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <p className="text-gray-600 mb-2">Email Address</p>
                    <p className="font-mono bg-gray-100 p-2 rounded">{result.email}</p>

                    <div className="mt-4">
                        <p className="text-gray-600 mb-2">Validation Status</p>
                        <div className={`flex items-center space-x-2 p-3 rounded-lg ${result.is_valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                            {result.is_valid ? (
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                                <XCircle className="w-6 h-6 text-red-600" />
                            )}
                            <span className={`font-semibold text-lg ${result.is_valid ? 'text-green-700' : 'text-red-700'}`}>
                                {result.is_valid ? 'VALID EMAIL' : 'INVALID EMAIL'}
                            </span>
                        </div>
                    </div>

                    <div className="mt-4">
                        <p className="text-gray-600 mb-2">Confidence Score</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${result.confidence_score * 100}%` }}
                            ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{Math.round(result.confidence_score * 100)}%</p>
                    </div>
                </div>

                <div>
                    <p className="text-gray-600 mb-2">Validation Details</p>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span>Syntax Valid:</span>
                            <span className={result.validation_details.syntax_valid ? 'text-green-600' : 'text-red-600'}>
                                {result.validation_details.syntax_valid ? 'Yes' : 'No'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>DNS Valid:</span>
                            <span className={result.validation_details.dns_valid ? 'text-green-600' : 'text-red-600'}>
                                {result.validation_details.dns_valid ? 'Yes' : 'No'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>Domain:</span>
                            <span className="font-mono text-sm">{result.validation_details.domain}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Disposable:</span>
                            <span className={result.validation_details.is_disposable ? 'text-red-600' : 'text-green-600'}>
                                {result.validation_details.is_disposable ? 'Yes' : 'No'}
                            </span>
                        </div>
                    </div>

                    {(result.validation_details.syntax_errors.length > 0 || result.validation_details.dns_errors.length > 0) && (
                        <div className="mt-4">
                            <p className="text-gray-600 mb-2">Issues Found</p>
                            <div className="bg-red-50 border border-red-200 rounded p-3">
                                {[...result.validation_details.syntax_errors, ...result.validation_details.dns_errors].map((error, index) => (
                                    <p key={index} className="text-red-700 text-sm">{error}</p>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Enhanced Risk Analysis */}
            {result.validation_details?.risk_analysis && (
                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                        <h4 className="font-semibold text-yellow-800">Detailed Risk Analysis</h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Risk Score</p>
                            <div className="flex items-center space-x-2">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full ${result.validation_details.risk_analysis.risk_score >= 60 ? 'bg-red-500' :
                                            result.validation_details.risk_analysis.risk_score >= 30 ? 'bg-yellow-500' :
                                                'bg-green-500'
                                            }`}
                                        style={{ width: `${Math.min(result.validation_details.risk_analysis.risk_score, 100)}%` }}
                                    ></div>
                                </div>
                                <span className="text-sm font-semibold">{result.validation_details.risk_analysis.risk_score}/100</span>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm text-gray-600 mb-1">Risk Factors Detected</p>
                            <p className="text-lg font-semibold text-gray-800">
                                {result.validation_details.risk_analysis.total_factors} factors
                            </p>
                        </div>
                    </div>

                    {result.validation_details.risk_analysis.risk_factors.length > 0 && (
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Identified Risk Factors:</p>
                            <ul className="space-y-1">
                                {result.validation_details.risk_analysis.risk_factors.map((factor, index) => (
                                    <li key={index} className="flex items-start space-x-2">
                                        <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <span className="text-sm text-gray-700">{factor}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Pattern Analysis Details */}
                    <div className="mt-4 pt-4 border-t border-yellow-200">
                        <p className="text-sm font-medium text-gray-700 mb-2">Pattern Analysis:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div>
                                <span className="text-gray-500">Local Length:</span>
                                <span className="ml-1 font-medium">{result.validation_details.risk_analysis.pattern_analysis.local_part_length}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Domain Type:</span>
                                <span className="ml-1 font-medium capitalize">{result.validation_details.risk_analysis.pattern_analysis.domain_type}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Numeric Density:</span>
                                <span className="ml-1 font-medium">{Math.round(result.validation_details.risk_analysis.pattern_analysis.numeric_density * 100)}%</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Suspicious Keywords:</span>
                                <span className="ml-1 font-medium">{result.validation_details.risk_analysis.pattern_analysis.has_suspicious_keywords ? 'Yes' : 'No'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AI Analysis Section */}
            {result.ai_analysis && (
                <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                        <Brain className="w-5 h-5 text-purple-600 mr-2" />
                        <h4 className="font-semibold text-purple-800">AI-Powered Security Analysis</h4>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{result.ai_analysis}</p>
                </div>
            )}
        </div>
    );

    const renderBulkResults = (data) => (
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Bulk Validation Results</h3>
                <div className="text-sm text-gray-500">
                    Processed in {data.processing_time.toFixed(2)}s
                </div>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{data.summary.total_processed}</div>
                    <div className="text-sm text-blue-700">Total Processed</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{data.summary.valid_count}</div>
                    <div className="text-sm text-green-700">Valid Emails</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{data.summary.invalid_count}</div>
                    <div className="text-sm text-red-700">Invalid Emails</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{data.summary.validation_rate}%</div>
                    <div className="text-sm text-purple-700">Success Rate</div>
                </div>
            </div>

            {/* Risk Distribution */}
            <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">Risk Distribution</h4>
                <div className="grid grid-cols-3 gap-4">
                    {Object.entries(data.summary.risk_distribution).map(([risk, count]) => (
                        <div key={risk} className="flex items-center justify-between bg-gray-50 rounded p-3">
                            <div className="flex items-center">
                                {getRiskIcon(risk)}
                                <span className="ml-2 capitalize">{risk}</span>
                            </div>
                            <span className="font-semibold">{count}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Top Domains */}
            {Object.keys(data.summary.domain_distribution).length > 0 && (
                <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">Top Domains</h4>
                    <div className="space-y-2">
                        {Object.entries(data.summary.domain_distribution).slice(0, 5).map(([domain, count]) => (
                            <div key={domain} className="flex justify-between items-center bg-gray-50 rounded p-2">
                                <span className="font-mono text-sm">{domain}</span>
                                <span className="font-semibold">{count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* AI Insights */}
            {data.ai_insights && (
                <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                        <Brain className="w-5 h-5 text-purple-600 mr-2" />
                        <h4 className="font-semibold text-purple-800">AI Insights</h4>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{data.ai_insights}</p>
                </div>
            )}

            {/* Individual Results */}
            <div>
                <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-800">Individual Results</h4>
                    <ExportCsvButton results={data.results} />
                </div>
                <div className="max-h-96 overflow-y-auto">
                    <div className="space-y-2">
                        {data.results.map((result, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 rounded p-3">
                                <div className="flex items-center space-x-3">
                                    {result.is_valid ? (
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                    ) : (
                                        <XCircle className="w-4 h-4 text-red-600" />
                                    )}
                                    <span className="font-mono text-sm">{result.email}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <span className={`text-sm ${getRiskColor(result.risk_level)}`}>
                                        {result.risk_level}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                        {Math.round(result.confidence_score * 100)}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
                <div className="container mx-auto px-4 py-8 max-w-4xl">
                    {/* Header */}
                    <div className="mb-8 rounded-2xl bg-white/60 p-6 shadow-sm backdrop-blur border border-white/70">
                        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                            <div>
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-xl shadow-sm">
                                        <Zap className="w-8 h-8 text-white" />
                                    </div>
                                    <div>
                                        <h1 className="text-3xl font-bold text-gray-800">AI Email Validator</h1>
                                        <p className="text-gray-500">AI-powered deliverability insights and security checks</p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                                    <span>Starter credits: {defaultCredits}</span>
                                    {user?.plan && (
                                        <span className="font-medium text-gray-700">Current plan: {user.plan}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col items-stretch gap-4">
                                <nav className="flex rounded-full bg-gray-100 p-1 text-sm font-medium">
                                    <button
                                        onClick={() => setActivePage('validate')}
                                        className={`flex-1 rounded-full px-4 py-2 transition ${activePage === 'validate'
                                            ? 'bg-white text-blue-600 shadow'
                                            : 'text-gray-600 hover:text-blue-600'
                                            }`}
                                    >
                                        Validator
                                    </button>
                                    <button
                                        onClick={() => setActivePage('pricing')}
                                        className={`flex-1 rounded-full px-4 py-2 transition ${activePage === 'pricing'
                                            ? 'bg-white text-blue-600 shadow'
                                            : 'text-gray-600 hover:text-blue-600'
                                            }`}
                                    >
                                        Pricing
                                    </button>
                                    <button
                                        onClick={() => setActivePage('Profile')}
                                        className={`flex-1 rounded-full px-4 py-2 transition ${activePage === 'Profile'
                                            ? 'bg-white text-blue-600 shadow'
                                            : 'text-gray-600 hover:text-blue-600'
                                            }`}
                                    >
                                        Profile
                                    </button>
                                </nav>
                                <div className="flex items-center justify-end gap-3">
                                    {user ? (
                                        <>
                                            <div className="flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-blue-700 shadow-sm">
                                                Credits
                                                <Coins className="w-4 h-4" /> :
                                                <span className="font-semibold">{user.credits}</span>
                                            </div>
                                            <button
                                                onClick={handleSignOut}
                                                className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow hover:text-blue-600"
                                            >
                                                <LogOut className="w-4 h-4" />
                                                Sign out
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-end gap-2">
                                            {authError && <span className="text-xs text-red-600">{authError}</span>}
                                            {authLoading ? (
                                                <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm text-gray-600 shadow">
                                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                                                    Signing in...
                                                </div>
                                            ) : (
                                                <GoogleLogin onSuccess={handleGoogleLoginSuccess} onError={handleGoogleLoginError} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {statusMessage && (
                        <div
                            className={`mb-6 rounded-xl border px-4 py-3 ${statusMessage.type === 'error'
                                ? 'border-red-200 bg-red-50 text-red-700'
                                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                }`}
                        >
                            {statusMessage.text}
                        </div>
                    )}

                    {activePage === 'validate' && (
                        <>
                            {/* Tab Navigation */}
                            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
                                <button
                                    onClick={() => setActiveTab('single')}
                                    className={`flex-1 flex items-center justify-center py-3 px-4 rounded-md transition-all ${activeTab === 'single'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-blue-600'
                                        }`}
                                >
                                    <Mail className="w-4 h-4 mr-2" />
                                    Single Email
                                </button>
                                <button
                                    onClick={() => setActiveTab('bulk')}
                                    className={`flex-1 flex items-center justify-center py-3 px-4 rounded-md transition-all ${activeTab === 'bulk'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-blue-600'
                                        }`}
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    Bulk Validation
                                </button>
                                <button
                                    onClick={() => setActiveTab('upload')}
                                    className={`flex-1 flex items-center justify-center py-3 px-4 rounded-md transition-all ${activeTab === 'upload'
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-blue-600'
                                        }`}
                                >
                                    <Upload className="w-4 h-4 mr-2" />
                                    File Upload
                                </button>
                            </div>

                            {/* Main Content */}
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                {/* AI Analysis Toggle with Better Explanation */}
                                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                                    <div className="flex items-center">
                                        <Brain className="w-5 h-5 text-purple-600 mr-2" />
                                        <div>
                                            <span className="font-medium text-gray-700">AI-Powered Security Analysis</span>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {includeAI
                                                    ? "Enhanced analysis with threat detection, pattern recognition, and domain reputation assessment"
                                                    : "Basic validation only - no AI security analysis"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className={`text-sm font-medium ${includeAI ? 'text-purple-600' : 'text-gray-500'}`}>
                                            {includeAI ? 'Enabled' : 'Disabled'}
                                        </span>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={includeAI}
                                                onChange={(e) => setIncludeAI(e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-['] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                        </label>
                                    </div>
                                </div>

                                {/* Single Email Tab */}
                                {activeTab === 'single' && (
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Single Email Validation</h2>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    value={singleEmail}
                                                    onChange={(e) => setSingleEmail(e.target.value)}
                                                    placeholder="Enter email address to validate"
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    onKeyPress={(e) => e.key === 'Enter' && validateSingleEmail()}
                                                />
                                            </div>
                                            <button
                                                onClick={validateSingleEmail}
                                                disabled={loading || !singleEmail.trim()}
                                                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                                            >
                                                {loading ? (
                                                    <div className="flex items-center space-x-2">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                        <span>
                                                            {includeAI ? 'Validating with AI Analysis...' : 'Validating...'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <CheckCircle className="w-5 h-5 mr-2" />
                                                        {includeAI ? 'Validate with AI Analysis' : 'Validate Email'}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Bulk Email Tab */}
                                {activeTab === 'bulk' && (
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Bulk Email Validation</h2>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email Addresses (one per line)
                                                </label>
                                                <textarea
                                                    value={bulkEmails}
                                                    onChange={(e) => setBulkEmails(e.target.value)}
                                                    rows={8}
                                                    placeholder="Enter email addresses, one per line..."
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                                <p className="text-sm text-gray-500 mt-2">
                                                    Maximum 1000 emails per batch
                                                </p>
                                            </div>
                                            <button
                                                onClick={validateBulkEmails}
                                                disabled={loading || !bulkEmails.trim()}
                                                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center"
                                            >
                                                {loading ? (
                                                    <div className="flex items-center space-x-2">
                                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                        <span>
                                                            {includeAI ? 'Processing with AI Analysis...' : 'Processing...'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <FileText className="w-5 h-5 mr-2" />
                                                        {includeAI ? 'Validate with AI Analysis' : 'Validate Emails'}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* File Upload Tab */}
                                {activeTab === 'upload' && (
                                    <div>
                                        <h2 className="text-xl font-semibold text-gray-800 mb-4">File Upload Validation</h2>
                                        <div className="space-y-4">
                                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-all">
                                                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                                <p className="text-gray-600 mb-4">
                                                    Upload a CSV or Excel file containing email addresses
                                                </p>
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleFileUpload}
                                                    accept=".csv,.xlsx,.xls"
                                                    className="hidden"
                                                />
                                                <button
                                                    onClick={() => fileInputRef.current?.click()}
                                                    disabled={loading}
                                                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-2 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition-all"
                                                >
                                                    {loading ? 'Processing...' : 'Choose File'}
                                                </button>
                                                {uploadedFile && (
                                                    <p className="text-sm text-gray-500 mt-2">
                                                        Selected: {uploadedFile.name}
                                                    </p>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                Supported formats: CSV, XLSX, XLS (Maximum 1000 emails)
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Results */}
                            {results && (
                                <>
                                    {results.type === 'single' && renderSingleResult(results.data)}
                                    {results.type === 'bulk' && renderBulkResults(results.data)}
                                    {results.type === 'upload' && renderBulkResults(results.data.validation_results)}
                                    {results.type === 'error' && (
                                        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mt-6">
                                            <div className="flex items-center mb-2">
                                                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                                                <h3 className="font-semibold text-red-800">Error</h3>
                                            </div>
                                            <p className="text-red-700">{results.data.message}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {activePage === 'pricing' && (
                        <div className="bg-white rounded-2xl shadow-lg p-6">
                            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                <div>
                                    <h2 className="text-2xl font-semibold text-gray-800">Flexible pricing for reliable outreach</h2>
                                    <p className="text-gray-500">Prices shown in {pricingCurrency}. Adjust the currency to match your market.</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Globe className="w-5 h-5 text-blue-500" />
                                    <select
                                        value={pricingCurrency}
                                        onChange={handleCurrencyChange}
                                        className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none"
                                    >
                                        {currencyOptions.map((option) => (
                                            <option key={option.code} value={option.code}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {pricingLoading && !pricingPlans.length ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                    <div className="mb-3 h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                                    Loading plans...
                                </div>
                            ) : (
                                pricingPlans.length ? (
                                    <div className="grid gap-6 md:grid-cols-3">
                                        {pricingPlans.map((plan) => (
                                            <div
                                                key={plan.plan_id}
                                                className={`rounded-2xl border p-6 shadow-sm transition ${plan.plan_id === 'growth'
                                                    ? 'border-purple-400 bg-purple-50/40'
                                                    : 'border-gray-200 bg-white'
                                                    }`}
                                            >
                                                <h3 className="text-lg font-semibold text-gray-800">{plan.name}</h3>
                                                <p className="mt-2 text-3xl font-bold text-gray-900">{formatPrice(plan.price_usd)}</p>
                                                <p className="text-sm text-gray-500">� {Number(plan.price_usd || 0).toFixed(2)} USD</p>
                                                <div className="mt-4">
                                                    <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                                                        {Number(plan.credits || 0).toLocaleString()} credits
                                                    </span>
                                                </div>
                                                <p className="mt-4 text-sm text-gray-600">{plan.description}</p>
                                                <button
                                                    onClick={() => handlePurchasePlan(plan.plan_id)}
                                                    disabled={pricingLoading && activePurchasePlan === plan.plan_id}
                                                    className="mt-6 w-full rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {pricingLoading && activePurchasePlan === plan.plan_id
                                                        ? 'Processing...'
                                                        : user
                                                            ? 'Purchase credits'
                                                            : 'Sign in to buy'}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-500">
                                        Pricing plans are temporarily unavailable. Please try again shortly.
                                    </div>
                                )
                            )}

                            <div className="mt-6 flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700">
                                <ShieldCheck className="w-5 h-5" />
                                <span>Credits never expire and cover single, bulk, and file validation workflows.</span>
                            </div>
                        </div>
                    )}

                    {activePage === 'Profile' && (
                        <Profile />
                    )}
                </div>
            </div>
        </>
    );
}

export default Home;























